import { useState } from "react";
import {
  SensorSetupCode,
  createSensorSetupCode,
} from "../../../Services/Admin/Sensors";
import "./SensorsPanel.css";

export default function SetupCodePanel() {
  const [expiresInMinutes, setExpiresInMinutes] = useState(30);
  const [created, setCreated] = useState<SensorSetupCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    setCreated(null);
    setCopied(false);
    try {
      setCreated(await createSensorSetupCode(expiresInMinutes));
    } catch (e: any) {
      setError(e?.message ?? "Could not create setup code");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.setupCode);
      setCopied(true);
    } catch {
      setError("Could not copy. Select the code manually.");
    }
  };

  return (
    <div className="admin-panel">
      <p className="admin-hint">
        Create a short-lived, single-use code before installing a new sensor —
        an ESP32 box or Beatspot Agent on the venue's computer
        (<code>beatspot-agent setup</code>). The permanent sensor token is
        generated during enrollment and is never entered by the installer.
      </p>

      <div className="admin-toolbar">
        <label className="admin-field setup-code-expiry">
          <span className="admin-label">Valid for</span>
          <select
            value={expiresInMinutes}
            onChange={(event) => setExpiresInMinutes(Number(event.target.value))}
            disabled={busy}
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={240}>4 hours</option>
          </select>
        </label>
        <button
          type="button"
          className="admin-btn--primary setup-code-create"
          onClick={create}
          disabled={busy}
        >
          {busy ? "Creating…" : "Create setup code"}
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {created && (
        <div className="admin-card setup-code-result" role="status">
          <span className="admin-label">One-time setup code</span>
          <code>{created.setupCode}</code>
          <p className="admin-hint">
            Expires {new Date(created.expiresAt).toLocaleString()}. It will not
            be shown again after this panel is closed or refreshed.
          </p>
          <button type="button" className="admin-btn--sm" onClick={copy}>
            {copied ? "Copied" : "Copy code"}
          </button>
        </div>
      )}
    </div>
  );
}
